import React, { useContext, useEffect, useState, useMemo } from 'react';
import { ConfigContext } from '../../../../../Contexts/ConfigContext';
import { UnitsContext } from '../../../../../Contexts/UnitsContext';
import Loader from '../../../../../Components/Loader';
import PageHeading from '../../../../../Components/PageHeading';
import CheckBoxInput from '../../../../../Components/CheckBoxInput';
import PlatformBannerConfig from './PlatformBannerConfig';
import { initialbvsConfig } from '../../../../../utilities/initialConfigs';
import {
	hasConfigKeys,
	shouldShowTgmrBackedConfig
} from '../../../../../utilities/tgmrSectionAdsHelpers';
import constants from '../constants.json';

//mui imports====
import useCommonStyles from '../../../../../commonStyles';
import Typography from '@mui/material/Typography';
import { Grid, Paper } from '@mui/material';
import { Box } from '@mui/material';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
//===============

const {
	platforms: { DESKTOP, MOBILE, TABLET },
	tgmrConstants: {
		RESPONSIVE,
		PLATFORM_DESKTOP,
		PLATFORM_MOBILE,
		AP_SECTIONID_DESKTOP,
		AP_SECTIONID_MOBILE,
		AP_SECTIONID_CARPET_MOBILE
	}
} = constants;

const BannerReplacementConfig = ({ keyData, featureDescription }) => {
	const commonStyleClasses = useCommonStyles();
	const { rawConfig, updateRawConfig } = useContext(ConfigContext); //rawConfig from ConfigContext
	const { tgmrDoc, updateTgmrDoc } = useContext(UnitsContext);
	const [selectedPlatform, setSelectedPlatform] = useState(DESKTOP);
	const [docFetchError, setDocFetchError] = useState('');
	const [isLoading, setIsLoading] = useState(true);

	let videoSectionId = rawConfig['videoSectionId'];
	const adsDoc = useMemo(() => {
		let ads = tgmrDoc?.ads?.filter((ad) => ad?.instreamSectionId === videoSectionId);
		return ads || [];
	}, [tgmrDoc, videoSectionId]);

	const hasAds = adsDoc.length > 0;
	const hasFeatureConfig = hasConfigKeys(rawConfig, keyData);
	const shouldShowConfigUi = shouldShowTgmrBackedConfig({ hasAds, hasFeatureConfig });

	// Fetching deskop Ap Tag Ad object from adDoc based upon the condition
	const desktopAdObj = useMemo(() => {
		let adObj = {};
		if (adsDoc && adsDoc.length > 0) {
			let validAdDoc = adsDoc.find((adDoc) => {
				return (
					adDoc?.formatData?.platform === RESPONSIVE &&
					adDoc?.formatData?.responsivePlatform === PLATFORM_DESKTOP
				);
			});

			if (validAdDoc) {
				adObj = validAdDoc;
			}
		}
		return adObj;
	}, [adsDoc]);

	// Fetching mobile Ap Tag Ad object from adDoc based upon the condition
	const mobileAdObj = useMemo(() => {
		let adObj = {};
		if (adsDoc && adsDoc.length > 0) {
			let validAdDoc = adsDoc.find((adDoc) => {
				return (
					adDoc?.formatData?.platform === RESPONSIVE &&
					adDoc?.formatData?.responsivePlatform === PLATFORM_MOBILE
				);
			});
			if (validAdDoc) {
				adObj = validAdDoc;
			}
		}
		return adObj;
	}, [adsDoc]);

	// Fetching optimized flying carpet Ap Tag Ad object from adDoc based upon the condition
	const ofcAdObj = useMemo(() => {
		let adObj = {};
		if (adsDoc && adsDoc.length > 0) {
			let validAdDoc = adsDoc.find((adDoc) => {
				return adDoc?.formatData?.platform === PLATFORM_MOBILE;
			});
			if (validAdDoc) {
				adObj = validAdDoc;
			}
		}
		return adObj;
	}, [adsDoc]);

	// The useEffect will be called when the component is rendering initially and when tgmrDoc gets updated, it fetches the tgmr doc from the api
	useEffect(() => {
		if (!tgmrDoc) {
			setIsLoading(true);
			updateTgmrDoc().finally(() => setIsLoading(false));
		} else {
			setIsLoading(false);
		}
	}, [tgmrDoc]);

	useEffect(() => {
		//Check if rawConfig have banner replacement config
		let error = '';
		let clonedRawConfig = structuredClone(rawConfig);

		if (hasAds && !hasFeatureConfig) {
			clonedRawConfig = { ...clonedRawConfig, ...initialbvsConfig };
		}

		// Updating ApTag sectionIds for different platforms to cloned raw config
		updateApTagsIds(selectedPlatform, clonedRawConfig);
		// Check if tgmrDoc is present for the current site Id else update error
		if (!shouldShowConfigUi) {
			error = 'Please create an ApTag to enable BVS';
		}
		setDocFetchError(error);
	}, [tgmrDoc, videoSectionId, hasAds, hasFeatureConfig, shouldShowConfigUi]);

	/**
	 * @name onPlatformChange
	 * @description The function is used to update the platform
	 * @param {string} newValue - It is the platform selected by user
	 * @param {function} event - It is the event being triggered when we update platform
	 */
	const onPlatformChange = (event, newValue) => {
		setSelectedPlatform(newValue);
		if (hasAds) {
			updateApTagsIds(newValue, structuredClone(rawConfig));
		}
	};

	/**
	 * @name updateApTagsIds
	 * @description The function is used to update the ap section Id for various platforms based upon the parameter in the object.
	 * @param {string} currentPlatform - It is the current platform for which section Id is to be updated
	 * @param {object} clonedRawConfig - It is the cloned raw config in which sectionIds will be appended.
	 */
	const updateApTagsIds = (currentPlatform, clonedRawConfig) => {
		if (currentPlatform === MOBILE || currentPlatform === TABLET) {
			// Check if mobile Ap Tag object is present in tgmr doc and id key is also present
			if (mobileAdObj && mobileAdObj.id) {
				const mobileKey = keyData + '.platforms.' + currentPlatform + '.' + AP_SECTIONID_MOBILE;
				clonedRawConfig[mobileKey] = mobileAdObj.id;
			}
			// Check if optimized flying carpet Ap Tag object is present in tgmr doc and id key is also present
			if (ofcAdObj && ofcAdObj.id) {
				const carpetMobileKey =
					keyData + '.platforms.' + currentPlatform + '.' + AP_SECTIONID_CARPET_MOBILE;
				clonedRawConfig[carpetMobileKey] = ofcAdObj.id;
			}
		}
		// Check if the platform is DESKTOP, desktop Ap Tag object is present and id key inside the Ap Tag object must be present
		if (currentPlatform === DESKTOP && desktopAdObj && desktopAdObj.id) {
			const desktopKey = keyData + '.platforms.' + currentPlatform + '.' + AP_SECTIONID_DESKTOP;
			clonedRawConfig[desktopKey] = desktopAdObj.id;
		}
		updateRawConfig(clonedRawConfig);
	};

	return (
		<Paper className={commonStyleClasses.mainPagePaperMarginStyle}>
			{isLoading ? (
				<Loader label="Loading Banner Replacement Ads Config..." />
			) : !shouldShowConfigUi ? (
				<Box
					sx={{
						marginTop: '4rem',
						paddingBottom: '15rem',
						textAlign: 'center',
						color: 'red',
						fontWeight: 'bold',
						fontSize: '2rem'
					}}
				>
					{docFetchError ||
						'No Banner replacement Ads Config found. Please create an ApTag to enable BVS Ads.'}
				</Box>
			) : (
				<>
					<PageHeading
						headingName="Banner Replacement Config"
						headingPath={['Dashboard', 'Framerate', 'Features Data', 'Banner Replacement Config']}
						description="Manage Platform Level Banner Replacement Config here"
						showInfoButton={true}
						infoMessage={featureDescription}
					/>
					<Grid container spacing={0} style={{ margin: '4rem 0 3rem 5rem', width: '90%' }}>
						<Grid item xs={6}>
							<CheckBoxInput
								keyData={keyData + '.enabled'}
								label="enabled"
								disabled={false}
								defaultChecked={false}
								object={rawConfig}
								updateObject={updateRawConfig}
							/>
						</Grid>
					</Grid>

					<TabContext value={selectedPlatform}>
						<Box
							sx={{
								borderBottom: 1,
								borderColor: 'divider',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center'
							}}
						>
							<TabList onChange={onPlatformChange}>
								<Tab label={DESKTOP} value={DESKTOP} />
								<Tab label={MOBILE} value={MOBILE} />
								<Tab label={TABLET} value={TABLET} />
							</TabList>
						</Box>
						<TabPanel value={DESKTOP}>
							<PlatformBannerConfig
								keyData={keyData + '.platforms.DESKTOP'}
								platform={DESKTOP}
								desktopAdObj={desktopAdObj}
							/>
						</TabPanel>
						<TabPanel value={MOBILE}>
							<PlatformBannerConfig
								keyData={keyData + '.platforms.MOBILE'}
								platform={MOBILE}
								mobileAdObj={mobileAdObj}
								ofcAdObj={ofcAdObj}
							/>
						</TabPanel>
						<TabPanel value={TABLET}>
							<PlatformBannerConfig
								keyData={keyData + '.platforms.TABLET'}
								platform={TABLET}
								mobileAdObj={mobileAdObj}
								ofcAdObj={ofcAdObj}
							/>
						</TabPanel>
					</TabContext>
				</>
			)}
		</Paper>
	);
};

export default BannerReplacementConfig;
